import { Box, Button, Stack, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { ParentOrganization } from '../../../data/models/parent-organization.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BadgeIsInternal from './BadgeIsInternal';
import { DateTime } from 'luxon';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCallback, useState } from 'react';
import ParentOrganizationDialog from './ParentOrganizationDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ParentOrganizationDetails';

const classes = {
  container: `${PREFIX}-container`,
  infoRow: `${PREFIX}-organization-info-row`,
  infoHeader: `${PREFIX}-organization-info-header`,
  infoTitle: `${PREFIX}-organization-info-title`,
  infoLabel: `${PREFIX}-organization-info-label`,
  infoValue: `${PREFIX}-organization-info-value`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    // alignItems: 'flex-start',
    margin: theme.spacing(2),
    // gap: theme.spacing(2),
    // flexWrap: 'wrap',
  },
  [`& .${classes.infoTitle}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.infoLabel}`]: {
    fontSize: 14,
    fontWeight: '400',
  },

  [`& .${classes.infoValue}`]: {
    fontSize: 15,
    fontWeight: '700',
  },
  [`& .${classes.infoRow}`]: {
    padding: '5px 0',
  },
}));

interface Props {
  organization?: ParentOrganization | null;
}

function ParentOrganizationDetails({ organization }: Props) {
  const [parentOrganizationDialogOpen, setParentOrganizationDialogOpen] =
    useState<boolean>(false);

  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();

  const renderDate = (value: string | null | undefined): React.ReactElement => {
    let date = '';
    if (value) {
      date = DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    }
    return <>{date}</>;
  };

  const handleEditOrganization = useCallback(() => {
    setParentOrganizationDialogOpen(true);
  }, []);

  const handleOrganizationDialogClose = useCallback(() => {
    setParentOrganizationDialogOpen(false);
  }, []);

  return (
    <StyledBox className={classes.container}>
      {organization && (
        <>
          <Paper elevation={1}>
            <Box sx={{ p: 2 }}>
              <Box className={classes.infoHeader}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}>
                  <Typography className={classes.infoTitle}>
                    {t('parentOrganizations.detailsTitle')}
                  </Typography>
                  {!isSuperReaderUser && (
                    <Button
                      variant="outlined"
                      endIcon={<EditOutlinedIcon />}
                      onClick={handleEditOrganization}>
                      {t('parentOrganizations.editOrganization')}
                    </Button>
                  )}
                </Stack>
              </Box>
              <Box className={classes.infoRow}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <CorporateFareOutlinedIcon />
                  <Box>
                    <Typography className={classes.infoLabel}>
                      {t('parentOrganizations.parentOrganizationBC')}
                    </Typography>
                    <Typography className={classes.infoValue}>
                      {organization?.parentOrganizationBC}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={classes.infoRow}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <BadgeOutlinedIcon />
                  <Box>
                    <Typography className={classes.infoLabel}>
                      {t('parentOrganizations.parentOrganizationName')}
                    </Typography>
                    <Typography className={classes.infoValue}>
                      {organization?.parentOrganizationName}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={classes.infoRow}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <ShieldOutlinedIcon />
                  <Box>
                    <Typography className={classes.infoLabel}>
                      {t('parentOrganizations.isInternal')}
                    </Typography>
                    <BadgeIsInternal isInternal={organization.isInternal} />
                  </Box>
                </Stack>
              </Box>
              <Box className={classes.infoRow}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <CalendarMonthOutlinedIcon />
                  <Box>
                    <Typography className={classes.infoLabel}>
                      {t('parentOrganizations.modifiedDateTime')}
                    </Typography>
                    <Typography className={classes.infoValue}>
                      {renderDate(organization?.modifiedDateTimeUTC)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Paper>
          <ParentOrganizationDialog
            open={parentOrganizationDialogOpen}
            organization={organization}
            onClose={handleOrganizationDialogClose}
            title={t('parentOrganizations.editOrganization')}
          />
        </>
      )}
    </StyledBox>
  );
}

export default ParentOrganizationDetails;
